import { TranslocoPageRouteSyncService } from '@/core/transloco/transloco-loader';
import { AdvancedSearchSelectComponent } from '@/shared/components/advanced-search/advanced-search-select.component';
import { IAdvancedSearchSelectConfig } from '@/shared/components/advanced-search/configs/advanced-search-select.config.interface';
import { ButtonComponent } from '@/shared/components/custom-elements/button/button.component';
import { DateInputComponent } from '@/shared/components/custom-elements/date-input/date-input.component';
import { InputComponent } from '@/shared/components/custom-elements/input/input.component';
import { RadioComponent } from '@/shared/components/custom-elements/radio/radio.component';
import { InfiniteDropdownComponent } from '@/shared/components/infinite-dropdown/infinite-dropdown.component';
import {
  InfiniteDropdownFetchFn,
  InfiniteDropdownQuery,
} from '@/shared/components/infinite-dropdown/infinite-dropdown.models';
import { DynamicListItemDto } from '@/shared/openapi/model/dynamic-list-item-dto';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { of } from 'rxjs';
import { startWith, switchMap, take } from 'rxjs/operators';
import { DropDownComponent } from '../../drop-down/drop-down.component';
import {
  FilterOperator,
  FilterType,
  MatTableFilter,
  MatTableFilterDynamicListConfig,
  MatTableFilterOption,
} from '../mat-table.types';

const OPERATOR_I18N_KEY: Record<FilterOperator, string> = {
  contains: 'components.mat-table.filter.operators.contains',
  equals: 'components.mat-table.filter.operators.equals',
  notEquals: 'components.mat-table.filter.operators.notEquals',
  startsWith: 'components.mat-table.filter.operators.startsWith',
  endsWith: 'components.mat-table.filter.operators.endsWith',
  greaterThan: 'components.mat-table.filter.operators.greaterThan',
  greaterOrEqual: 'components.mat-table.filter.operators.greaterOrEqual',
  lessThan: 'components.mat-table.filter.operators.lessThan',
  lessOrEqual: 'components.mat-table.filter.operators.lessOrEqual',
  between: 'components.mat-table.filter.operators.between',
  in: 'components.mat-table.filter.operators.in',
  notIn: 'components.mat-table.filter.operators.notIn',
  before: 'components.mat-table.filter.operators.before',
  after: 'components.mat-table.filter.operators.after',
  isNull: 'components.mat-table.filter.operators.isNull',
};

function operatorLabelKey(operator: FilterOperator, type: FilterType): string {
  if (type === 'date' && operator === 'equals') {
    return 'components.mat-table.filter.operators.onDate';
  }
  if (type === 'boolean' && operator === 'equals') {
    return 'components.mat-table.filter.operators.is';
  }
  return OPERATOR_I18N_KEY[operator];
}

/**
 * Filter operators configuration
 * Extend this map to add new filter types or operators without modifying getOperatorsForType
 */
const FILTER_OPERATORS_CONFIG: Record<
  FilterType,
  { value: FilterOperator; label: string }[]
> = {
  text: [
    { value: 'contains', label: OPERATOR_I18N_KEY.contains },
    { value: 'equals', label: OPERATOR_I18N_KEY.equals },
    { value: 'notEquals', label: OPERATOR_I18N_KEY.notEquals },
    { value: 'isNull', label: OPERATOR_I18N_KEY.isNull },
  ],
  number: [
    { value: 'equals', label: OPERATOR_I18N_KEY.equals },
    { value: 'notEquals', label: OPERATOR_I18N_KEY.notEquals },
    { value: 'greaterThan', label: OPERATOR_I18N_KEY.greaterThan },
    { value: 'greaterOrEqual', label: OPERATOR_I18N_KEY.greaterOrEqual },
    { value: 'lessThan', label: OPERATOR_I18N_KEY.lessThan },
    { value: 'lessOrEqual', label: OPERATOR_I18N_KEY.lessOrEqual },
    { value: 'between', label: OPERATOR_I18N_KEY.between },
    { value: 'isNull', label: OPERATOR_I18N_KEY.isNull },
  ],
  date: [
    { value: 'equals', label: 'components.mat-table.filter.operators.onDate' },
    { value: 'before', label: OPERATOR_I18N_KEY.before },
    { value: 'after', label: OPERATOR_I18N_KEY.after },
    { value: 'greaterOrEqual', label: OPERATOR_I18N_KEY.greaterOrEqual },
    { value: 'lessOrEqual', label: OPERATOR_I18N_KEY.lessOrEqual },
    { value: 'between', label: OPERATOR_I18N_KEY.between },
    { value: 'isNull', label: OPERATOR_I18N_KEY.isNull },
  ],
  boolean: [
    { value: 'equals', label: 'components.mat-table.filter.operators.is' },
    { value: 'notEquals', label: OPERATOR_I18N_KEY.notEquals },
    { value: 'isNull', label: OPERATOR_I18N_KEY.isNull },
  ],
  select: [{ value: 'equals', label: OPERATOR_I18N_KEY.equals }],
  multiselect: [{ value: 'in', label: OPERATOR_I18N_KEY.equals }],
  infiniteMultiselect: [{ value: 'in', label: OPERATOR_I18N_KEY.equals }],
  advancedSearch: [{ value: 'equals', label: OPERATOR_I18N_KEY.equals }],
};

export function resolveColumnFilterOperators(
  type: FilterType,
  allowedOperators?: FilterOperator[] | null
): { value: FilterOperator; label: string }[] {
  if (allowedOperators?.length) {
    return allowedOperators.map(value => ({
      value,
      label: operatorLabelKey(value, type),
    }));
  }
  const configured = FILTER_OPERATORS_CONFIG[type];
  if (configured?.length) {
    return configured;
  }
  return [{ value: 'contains', label: OPERATOR_I18N_KEY.contains }];
}

/**
 * Column filter component for filtering table data
 * Supports multiple filter types: text, number, date, boolean, select, multiselect
 */
@Component({
  selector: 'app-column-filter',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    MatCheckboxModule,
    MatRadioModule,
    TranslocoPipe,
    InputComponent,
    DateInputComponent,
    ButtonComponent,
    RadioComponent,
    AdvancedSearchSelectComponent,
    InfiniteDropdownComponent,
    DropDownComponent,
  ],
  templateUrl: './column-filter.component.html',
  styleUrl: './column-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColumnFilterComponent implements OnInit {
  private readonly transloco = inject(TranslocoService);
  private readonly pageI18n = inject(TranslocoPageRouteSyncService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  // Inputs
  readonly columnId = input.required<string>();
  readonly columnLabel = input.required<string>();
  readonly filterType = input<FilterType>('text');
  readonly currentFilter = input<MatTableFilter | null>(null);
  readonly filterOptions = input<MatTableFilterOption[]>([]);
  readonly advancedSearchConfig = input<IAdvancedSearchSelectConfig | null>(
    null
  );
  /** Catalogue edit on filter value picker; off by default for table filters. */
  readonly advancedSearchCanEdit = input(false);
  readonly filterDynamicList = input<MatTableFilterDynamicListConfig | null>(
    null
  );
  readonly allowedOperators = input<FilterOperator[] | null>(null);
  readonly menuTrigger = input<MatMenuTrigger | undefined>(undefined);

  // Outputs
  readonly filterApplied = output<MatTableFilter>();
  readonly filterCleared = output<void>();

  // Computed column label (already translated by parent)
  readonly translatedColumnLabel = computed(() => {
    const label = this.columnLabel();
    return label;
  });

  // Filter form state
  readonly filterForm = new FormGroup({
    operator: new FormControl<FilterOperator>('contains'),
    value: new FormControl<unknown>(''),
    valueTo: new FormControl<unknown>(''), // For "between" operations
  });

  // UI state
  readonly isAdvancedMode = signal<boolean>(false);

  /** Computed (not effect + signal) so the first paint includes options; effects run after CD and left date/number selects empty. */
  readonly availableOperators = computed(() =>
    resolveColumnFilterOperators(this.filterType(), this.allowedOperators())
  );
  readonly translatedFilterOptions = computed(() => {
    // Re-run when the active language changes.
    this.transloco.getActiveLang();
    return this.filterOptions().map(option => ({
      ...option,
      label: this.translateFilterOptionLabel(option.label),
    }));
  });

  @ViewChild('multiSelect') multiSelect?: MatSelect;
  @ViewChild('infiniteMultiselectDropdown')
  infiniteMultiselectDropdown?: InfiniteDropdownComponent<DynamicListItemDto>;

  readonly infiniteMultiselectPrefetchedRows = signal<DynamicListItemDto[]>([]);

  /** Delegates to {@link MatTableFilterDynamicListConfig.fetchPage}. */
  readonly listItemsFetchPage: InfiniteDropdownFetchFn<DynamicListItemDto> = (
    q: InfiniteDropdownQuery
  ) => {
    const fetchPage = this.filterDynamicList()?.fetchPage;
    if (!fetchPage) {
      return of({ rows: [] as DynamicListItemDto[], count: 0 });
    }
    return fetchPage(q);
  };

  readonly dynamicListLabelAccessor = (item: DynamicListItemDto) =>
    item.value ?? '';
  readonly dynamicListValueAccessor = (item: DynamicListItemDto) => item.id;

  constructor() {
    // Update available operators when filter type changes
    effect(() => {
      const type = this.filterType();
      this.allowedOperators();
      if (this.currentFilter()) return;
      const defaultOp = this.getDefaultOperator(type);
      if (this.filterForm.value.operator !== defaultOp) {
        this.filterForm.patchValue(
          { operator: defaultOp },
          { emitEvent: false }
        );
      }
    });

    // Load current filter if exists (and handle type-specific defaults)
    effect(() => {
      const currentFilter = this.currentFilter();
      const type = this.filterType();

      if (currentFilter) {
        let value: unknown = currentFilter.value;
        let valueTo: unknown = '';

        if (
          currentFilter.operator === 'between' &&
          value &&
          typeof value === 'object'
        ) {
          const range = value as { from?: unknown; to?: unknown };
          value = range.from ?? '';
          valueTo = range.to ?? '';
        }

        if (type === 'multiselect' || type === 'infiniteMultiselect') {
          value = Array.isArray(value)
            ? value
            : [value].filter(v => v !== undefined && v !== null && v !== '');
        }
        if (type === 'advancedSearch' && typeof value === 'string') {
          value = { id: value };
        }

        const next = {
          operator: currentFilter.operator,
          value,
          valueTo,
        };
        if (!this.filterFormValuesEqual(next)) {
          this.filterForm.patchValue(next, { emitEvent: false });
        }
      } else {
        const emptyValue =
          type === 'multiselect' || type === 'infiniteMultiselect'
            ? []
            : type === 'advancedSearch'
              ? null
              : '';
        const next = {
          operator: this.getDefaultOperator(type),
          value: emptyValue as unknown,
          valueTo: '',
        };
        if (!this.filterFormValuesEqual(next)) {
          this.filterForm.patchValue(next, { emitEvent: false });
        }
      }
    });

    effect(() => {
      const type = this.filterType();
      const filter = this.currentFilter();
      if (type !== 'infiniteMultiselect' || !this.filterDynamicList()) {
        this.infiniteMultiselectPrefetchedRows.set([]);
        return;
      }
      const raw = filter?.value;
      const ids = Array.isArray(raw)
        ? raw.filter((v): v is string => typeof v === 'string' && v !== '')
        : typeof raw === 'string' && raw !== ''
          ? [raw]
          : [];
      if (!ids.length) {
        this.infiniteMultiselectPrefetchedRows.set([]);
        return;
      }
      this.listItemsFetchPage({ skip: 0, limit: 100, search: '' })
        .pipe(take(1))
        .subscribe(page => {
          const rows = (page.rows ?? []).filter(row => ids.includes(row.id));
          this.infiniteMultiselectPrefetchedRows.set(rows);
        });
    });

    // Advanced search CVA expects null, not empty string (default FormControl value).
    effect(() => {
      if (this.filterType() !== 'advancedSearch') return;
      const ctrl = this.filterForm.get('value');
      if (ctrl && ctrl.value === '') {
        ctrl.setValue(null, { emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    this.transloco.langChanges$
      .pipe(
        startWith(this.transloco.getActiveLang()),
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.pageI18n.ensureKnownComponentBundle('mat-table'))
      )
      .subscribe(() => this.cdr.markForCheck());
  }

  /**
   * Get default operator for a filter type
   */
  private getDefaultOperator(type: FilterType): FilterOperator {
    const allowed = this.allowedOperators();
    if (allowed?.length) {
      if (type === 'text' && allowed.includes('contains')) return 'contains';
      if (allowed.includes('equals')) return 'equals';
      if (type === 'infiniteMultiselect' && allowed.includes('in')) return 'in';
      return allowed[0];
    }
    switch (type) {
      case 'text':
        return 'contains';
      case 'number':
      case 'date':
      case 'boolean':
      case 'select':
        return 'equals';
      case 'multiselect':
      case 'infiniteMultiselect':
        return 'in';
      case 'advancedSearch':
        return 'equals';
      default:
        return 'contains';
    }
  }

  /**
   * Apply the filter
   */
  applyFilter(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.infiniteMultiselectDropdown?.closePanel();

    const formValue = this.filterForm.value;
    const operator = formValue.operator as FilterOperator;
    let value = formValue.value;
    const filterTypeNow = this.filterType();

    if (filterTypeNow === 'infiniteMultiselect') {
      value = this.infiniteMultiselectDropdown?.getValue() ?? value;
    }

    // Handle "between" operator
    if (operator === 'between') {
      value = {
        from: formValue.value,
        to: formValue.valueTo,
      };
    }

    // Don't apply if value is empty (except for boolean / is-empty)
    if (
      operator !== 'isNull' &&
      filterTypeNow !== 'boolean' &&
      filterTypeNow !== 'advancedSearch' &&
      (!value || (Array.isArray(value) && value.length === 0))
    ) {
      return;
    }

    if (
      filterTypeNow === 'boolean' &&
      operator !== 'isNull' &&
      typeof value !== 'boolean'
    ) {
      return;
    }

    if (filterTypeNow === 'advancedSearch') {
      if (value == null || value === '') {
        return;
      }
      if (typeof value === 'string' && !String(value).trim()) {
        return;
      }
      if (typeof value === 'object') {
        const id = (value as { id?: unknown }).id;
        if (id == null || String(id).trim() === '') {
          return;
        }
      }
    }

    // Multi-select sends an array and uses the "in" operator.
    if (
      filterTypeNow === 'multiselect' ||
      filterTypeNow === 'infiniteMultiselect'
    ) {
      value = Array.isArray(value) ? value : [value].filter(Boolean);
    }

    const filter: MatTableFilter = {
      value,
      operator,
      type: filterTypeNow,
    };

    this.filterApplied.emit(filter);
    setTimeout(() => this.closeFilterMenu(), 0);
  }

  /**
   * Clear the filter
   */
  clearFilter(): void {
    this.infiniteMultiselectDropdown?.closePanel();
    const type = this.filterType();
    const emptyValue =
      type === 'multiselect' || type === 'infiniteMultiselect'
        ? []
        : type === 'advancedSearch'
          ? null
          : '';
    this.filterForm.reset({
      operator: this.getDefaultOperator(type),
      value: emptyValue as unknown,
      valueTo: '',
    });
    this.filterCleared.emit();
    setTimeout(() => this.closeFilterMenu(), 0);
  }

  private closeFilterMenu(): void {
    const trigger = this.menuTrigger();
    if (trigger) {
      trigger.closeMenu();
    }
  }

  isSelectAllAction(): boolean {
    const type = this.filterType();
    return type === 'multiselect' || type === 'infiniteMultiselect';
  }

  onSecondaryAction(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const type = this.filterType();

    if (type === 'multiselect') {
      const allValues = this.filterOptions().map(option => option.value);
      this.filterForm.patchValue({
        operator: 'in',
        value: allValues,
      });
      this.applyFilter();
      return;
    }

    // For infinite multiselect we do not hold the complete option dataset locally.
    // Clearing the column filter keeps all rows visible (equivalent to "select all").
    this.clearFilter();
  }

  /**
   * When opening a multiselect, clear the initial active option highlight so
   * nothing appears pre-selected when no value is chosen.
   */
  onMultiSelectOpened(opened: boolean): void {
    if (!opened) return;
    if (this.filterType() !== 'multiselect') return;

    this.clearMultiSelectActiveState();
  }

  onMultiSelectSelectionChange(): void {
    if (this.filterType() !== 'multiselect') return;
    this.clearMultiSelectActiveState();
  }

  private clearMultiSelectActiveState(): void {
    setTimeout(() => {
      try {
        const select = this.multiSelect as MatSelect & {
          _keyManager?: { setActiveItem: (index: number) => void };
        };
        // Keep no option in "active/focused" visual state after open/select/unselect.
        select._keyManager?.setActiveItem(-1);

        const panelEl = select.panel?.nativeElement as HTMLElement | undefined;
        if (!panelEl) return;
        panelEl
          .querySelectorAll('.mat-mdc-option-active')
          .forEach(el => el.classList.remove('mat-mdc-option-active'));
      } catch {
        // Fail silently if Material internals change
      }
    }, 0);
  }

  /**
   * Check if "between" operator is selected
   */
  isBetweenOperator(): boolean {
    return this.filterForm.value.operator === 'between';
  }

  isNullOperator(): boolean {
    return this.filterForm.value.operator === 'isNull';
  }

  onBooleanValueChange(value: unknown): void {
    this.filterForm.controls.value.setValue(value);
    this.cdr.markForCheck();
  }

  private translateFilterOptionLabel(label: string): string {
    const raw = (label ?? '').trim();
    if (!raw) return '';

    const translated = this.transloco.translate(raw);
    return translated !== raw ? translated : raw;
  }

  private filterFormValuesEqual(next: {
    operator: FilterOperator | null | undefined;
    value: unknown;
    valueTo: unknown;
  }): boolean {
    const current = this.filterForm.value;
    if (current.operator !== next.operator) {
      return false;
    }
    if (current.valueTo !== next.valueTo) {
      return false;
    }
    const curVal = current.value;
    const nextVal = next.value;
    if (Array.isArray(curVal) && Array.isArray(nextVal)) {
      return (
        curVal.length === nextVal.length &&
        curVal.every((v, i) => v === nextVal[i])
      );
    }
    return curVal === nextVal;
  }

  /**
   * Toggle advanced mode
   */
  toggleAdvancedMode(): void {
    this.isAdvancedMode.update(mode => !mode);
  }
}
